import { useCallback, useMemo } from 'react';
import { api } from '../../utils/api';
import API_ENDPOINTS from '../../config/apiConfig';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { useUrlBackedState } from './useReportUrlState';

/**
 * Per-table column customization for the native report pages.
 *
 * Precedence: URL override (`c_<tableKey>` param — shared links carry the
 * sender's column view) → user-saved default (userSettings.reportTableColumns,
 * persisted like journalExportColumns and projected through initData) →
 * built-in default (all columns).
 *
 * Returns a `columnPrefs` object the kit's ReportMatrix / ReportDetailTable
 * render as a Columns picker + an orange "Customized · Reset" pill whenever
 * the active set differs from the user's default.
 *
 *   const columnPrefs = useColumnPrefs('revForecast', tableColumns);
 *   <ReportMatrix columns={tableColumns} columnPrefs={columnPrefs} … />
 */

// group/facet composite-key separator. A NUL escape (never present in group
// labels or facet ids) keeps composite keys collision-free while staying
// textual in source — a raw NUL byte would make git treat this file as binary.
const GF = '\u0000';

/**
 * When columns declare a `facet` (metric id, e.g. 'actual'|'budget'|'variance')
 * that repeats across multiple `group`s (months + Total), the picker collapses
 * to one control per facet that applies to every group at once. Returns null
 * for flat/single-group/single-metric tables so they keep the per-column picker.
 */
function buildFacetModel(columns) {
  const cols = columns || [];
  if (!cols.some((c) => c && c.facet != null)) return null;
  const groups = [];
  const facetOrder = [];
  const facetLabel = new Map();
  const facetDescription = new Map();
  const keyByGroupFacet = new Map();
  const facetByKey = new Map();
  const groupsByFacet = new Map();
  cols.forEach((c) => {
    if (!c || c.facet == null) return;
    const g = c.group ?? '';
    const f = c.facet;
    if (!groups.includes(g)) groups.push(g);
    if (!facetOrder.includes(f)) {
      facetOrder.push(f);
      facetLabel.set(f, c.label ?? String(f));
    }
    if (c.description && !facetDescription.has(f)) facetDescription.set(f, c.description);
    keyByGroupFacet.set(`${g}${GF}${f}`, c.key);
    facetByKey.set(c.key, f);
    if (!groupsByFacet.has(f)) groupsByFacet.set(f, new Set());
    groupsByFacet.get(f).add(g);
  });
  // Only collapse when >1 metric genuinely repeats across >1 group — otherwise
  // a single-metric picker (e.g. actuals-only, no budget) would be an inert,
  // always-checked control and lose the per-column month show/hide.
  const spans = facetOrder.some((f) => (groupsByFacet.get(f)?.size || 0) > 1);
  if (!spans || groups.length < 2 || facetOrder.length < 2) return null;
  return { groups, facetOrder, facetLabel, facetDescription, keyByGroupFacet, facetByKey };
}

/** Facets currently visible, in the order they appear in the flat key list. */
function currentVisibleFacets(keys, facetModel) {
  const seen = [];
  (keys || []).forEach((k) => {
    const f = facetModel.facetByKey.get(k);
    if (f !== undefined && !seen.includes(f)) seen.push(f);
  });
  return seen;
}

/**
 * Flat, group-contiguous key list from an ordered facet sequence — group-major,
 * facet-minor — so every group stays contiguous (ReportMatrix's column-group
 * headers require it) and shares the same metric order.
 */
function expandFacetKeys(facetModel, facetSeq) {
  const keys = [];
  facetModel.groups.forEach((g) => {
    facetSeq.forEach((f) => {
      const k = facetModel.keyByGroupFacet.get(`${g}${GF}${f}`);
      if (k) keys.push(k);
    });
  });
  return keys;
}

export function useColumnPrefs(tableKey, columns) {
  const { userSettings, updateSettings } = useUserSettingsContext();
  const [urlValue, setUrlValue] = useUrlBackedState(`c_${tableKey}`, null, 'strings');

  const allKeys = useMemo(() => (columns || []).map((c) => c.key), [columns]);
  // Built-in default: every column except the opt-in ones (`defaultOff`, e.g.
  // the P&L's % Rev / $POR / $PAR metrics). They stay listed in the picker and
  // can be enabled/saved like any other column.
  const builtInKeys = useMemo(() => {
    const keys = (columns || []).filter((c) => !c.defaultOff).map((c) => c.key);
    return keys.length > 0 ? keys : (columns || []).map((c) => c.key);
  }, [columns]);
  const facetModel = useMemo(() => buildFacetModel(columns), [columns]);

  // Resolve a persisted/shared key list against the CURRENT columns. Faceted
  // tables (e.g. the P&L) express a per-metric preference, but the flat keys are
  // month-specific — so after a period change the raw month keys no longer match
  // and a plain filter would strip every month, silently collapsing the matrix
  // to the (month-independent) Total group. Instead recover the metric (facet)
  // selection + order and re-expand it onto the current month groups.
  const resolveKeys = useCallback(
    (raw) => {
      if (!Array.isArray(raw) || raw.length === 0) return null;
      if (facetModel) {
        const seq = currentVisibleFacets(raw, facetModel);
        if (seq.length === 0) return null;
        const keys = expandFacetKeys(facetModel, seq);
        return keys.length > 0 ? keys : null;
      }
      const valid = raw.filter((k) => allKeys.includes(k));
      return valid.length > 0 ? valid : null;
    },
    [facetModel, allKeys]
  );

  const savedKeys = useMemo(
    () => resolveKeys(userSettings?.reportTableColumns?.[tableKey]),
    [resolveKeys, userSettings, tableKey]
  );

  const defaultKeys = savedKeys || builtInKeys;
  const visibleKeys = useMemo(() => resolveKeys(urlValue) || defaultKeys, [resolveKeys, urlValue, defaultKeys]);

  // Order-sensitive: reordering counts as customization too.
  const sameSequence = (a, b) => a.length === b.length && a.every((k, i) => b[i] === k);
  const isCustomized = !sameSequence(visibleKeys, defaultKeys);
  const hasSavedDefault = savedKeys !== null;

  const toggleColumn = useCallback(
    (key) => {
      setUrlValue(() => {
        const current = visibleKeys;
        if (current.includes(key)) {
          const next = current.filter((k) => k !== key);
          return next.length === 0 ? current : next; // never hide everything
        }
        // re-enable at the column's built-in position among the visible set
        const builtInIdx = allKeys.indexOf(key);
        const insertAt = current.findIndex((k) => allKeys.indexOf(k) > builtInIdx);
        const next = [...current];
        next.splice(insertAt === -1 ? next.length : insertAt, 0, key);
        return next;
      });
    },
    [setUrlValue, visibleKeys, allKeys]
  );

  /** Move a visible column left/right (dir -1 | +1) — order persists in URL/profile. */
  const moveColumn = useCallback(
    (key, dir) => {
      setUrlValue(() => {
        const current = [...visibleKeys];
        const idx = current.indexOf(key);
        const target = idx + dir;
        if (idx === -1 || target < 0 || target >= current.length) return current;
        [current[idx], current[target]] = [current[target], current[idx]];
        return current;
      });
    },
    [setUrlValue, visibleKeys]
  );

  /** Replace the whole visible sequence (drag-and-drop reorder). */
  const reorderColumns = useCallback(
    (nextKeys) => {
      const valid = (nextKeys || []).filter((k) => allKeys.includes(k));
      if (valid.length > 0) setUrlValue(valid);
    },
    [setUrlValue, allKeys]
  );

  /* ---------- facet-collapsed picker (metrics span all month groups) ---------- */

  const rebuildFacetKeys = useCallback(
    (facetSeq) => (facetModel ? expandFacetKeys(facetModel, facetSeq) : visibleKeys),
    [facetModel, visibleKeys]
  );

  const toggleFacet = useCallback(
    (facet) => {
      if (!facetModel) return;
      setUrlValue(() => {
        const vis = currentVisibleFacets(visibleKeys, facetModel);
        let next;
        if (vis.includes(facet)) {
          next = vis.filter((f) => f !== facet);
          if (next.length === 0) return visibleKeys; // never hide every metric
        } else {
          // re-enable at the metric's built-in position among the visible set
          const builtIn = facetModel.facetOrder.indexOf(facet);
          const at = vis.findIndex((f) => facetModel.facetOrder.indexOf(f) > builtIn);
          next = [...vis];
          next.splice(at === -1 ? next.length : at, 0, facet);
        }
        return rebuildFacetKeys(next);
      });
    },
    [facetModel, visibleKeys, setUrlValue, rebuildFacetKeys]
  );

  const reorderFacets = useCallback(
    (nextVisibleFacets) => {
      if (!facetModel) return;
      const seq = (nextVisibleFacets || []).filter((f) => facetModel.facetOrder.includes(f));
      if (seq.length > 0) setUrlValue(rebuildFacetKeys(seq));
    },
    [facetModel, setUrlValue, rebuildFacetKeys]
  );

  const facets = useMemo(() => {
    if (!facetModel) return null;
    const visibleFacets = currentVisibleFacets(visibleKeys, facetModel);
    const hidden = facetModel.facetOrder.filter((f) => !visibleFacets.includes(f));
    const items = [...visibleFacets, ...hidden].map((f) => ({
      key: f,
      label: facetModel.facetLabel.get(f),
      description: facetModel.facetDescription.get(f) || null,
      checked: visibleFacets.includes(f),
    }));
    return { items, visibleFacets, toggleFacet, reorderFacets };
  }, [facetModel, visibleKeys, toggleFacet, reorderFacets]);

  /** Back to the user's default (saved default if present, else all columns). */
  const resetToDefault = useCallback(() => setUrlValue(null), [setUrlValue]);

  /** Persist the current visible set as the user's default for this table. */
  const saveAsDefault = useCallback(async () => {
    const next = { ...(userSettings?.reportTableColumns || {}), [tableKey]: visibleKeys };
    await api.patch(API_ENDPOINTS.USER_SETTINGS, {
      ...userSettings,
      id: userSettings.email,
      reportTableColumns: next,
    });
    await updateSettings({ reportTableColumns: next });
    setUrlValue(null); // current view IS the default now
  }, [userSettings, updateSettings, tableKey, visibleKeys, setUrlValue]);

  /** Remove the saved default (back to all columns). */
  const clearSavedDefault = useCallback(async () => {
    const next = { ...(userSettings?.reportTableColumns || {}) };
    delete next[tableKey];
    await api.patch(API_ENDPOINTS.USER_SETTINGS, {
      ...userSettings,
      id: userSettings.email,
      reportTableColumns: next,
    });
    await updateSettings({ reportTableColumns: next });
    setUrlValue(null);
  }, [userSettings, updateSettings, tableKey, setUrlValue]);

  return {
    tableKey,
    allColumns: columns || [],
    visibleKeys,
    isCustomized,
    hasSavedDefault,
    toggleColumn,
    moveColumn,
    reorderColumns,
    facets, // non-null when the picker should collapse to per-metric controls
    resetToDefault,
    saveAsDefault,
    clearSavedDefault,
  };
}

export default useColumnPrefs;
